import { startCase } from "lodash";
import { FC, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  CheckboxTemplate,
  DropdownBoundTemplate,
  DropdownTemplate,
  GroupTitleTemplate,
  TimeTemplate,
} from "@ryde/controls";
import {
  BOOKING_BULK_FORM_FIELDS,
  DirectionEnum,
  DIRECTIONS_INCLUDE_BOTH,
  PLURAL_KEY,
  requiredRuleStringField,
  selectedPassengersBookingsSelector,
  SHOWN_PASSENGER_NAMES_LENGTH,
  systemSettingsDtoAtom,
  useFormats,
} from "@ryde/shared";

import { DrawerFormWrapperStyled } from "../../drawer";
import {
  BulkBookingHeaderPassengersStyled,
  BulkConflictsSection,
  BulkCreateBookingsFormBlockStyled,
  BulkCreateBookingsPassengersBodyStyled,
  BulkCreateBookingsPassengersHeaderStyled,
  BulkCreateBookingsTimeBlockStyled,
  SourceDestinationDropdowns,
  UnaffiliatedUnitsAlert,
  useBulkBookingForm,
  useBulkBookingFormEffects,
  useUnaffiliatedUnitsWarning,
} from "../bulk-bookings-components";

export const BulkCreateBookingsForm: FC = () => {
  const { t } = useTranslation();
  const { timeFormat } = useFormats();
  const { watch } = useFormContext();
  const selectedPassengers = useRecoilValue(selectedPassengersBookingsSelector);
  const systemSettingsDto = useRecoilValue(systemSettingsDtoAtom);
  const { disableShiftsDropdown, useFoods, enableFlights } =
    systemSettingsDto?.metadata ?? {};
  const { onShiftSelect } = useBulkBookingForm();

  const direction = watch(BOOKING_BULK_FORM_FIELDS.direction);

  const passengerNames = useMemo(() => {
    return selectedPassengers
      .slice(0, SHOWN_PASSENGER_NAMES_LENGTH)
      .map(e => e.passengerName)
      .join(", ");
  }, [selectedPassengers]);

  const passengerIds = useMemo(() => {
    return selectedPassengers.map(e => e.passengerId).filter(Boolean);
  }, [selectedPassengers]);

  const { hasUnaffiliatedUnits } = useUnaffiliatedUnitsWarning({
    passengerItems: selectedPassengers,
    shiftFieldName: BOOKING_BULK_FORM_FIELDS.shiftId,
  });

  useBulkBookingFormEffects({
    direction,
  });

  return (
    <DrawerFormWrapperStyled>
      {hasUnaffiliatedUnits && <UnaffiliatedUnitsAlert />}
      <BulkBookingHeaderPassengersStyled>
        <BulkCreateBookingsPassengersHeaderStyled>
          <span>{startCase(t(`passenger.${PLURAL_KEY}`))}</span>
          <span>{selectedPassengers.length}</span>
        </BulkCreateBookingsPassengersHeaderStyled>
        <BulkCreateBookingsPassengersBodyStyled>
          <span>{passengerNames}</span>
          <span>
            {selectedPassengers.length > SHOWN_PASSENGER_NAMES_LENGTH
              ? `+${selectedPassengers.length - SHOWN_PASSENGER_NAMES_LENGTH}`
              : ""}
          </span>
        </BulkCreateBookingsPassengersBodyStyled>
      </BulkBookingHeaderPassengersStyled>
      {!disableShiftsDropdown && (
        <GroupTitleTemplate
          name="shifts"
          formLabel={startCase(t(`shift.${PLURAL_KEY}`))}
        />
      )}
      <BulkCreateBookingsFormBlockStyled
        $paddingTop={disableShiftsDropdown ? "24px" : "auto"}
      >
        {!disableShiftsDropdown && (
          <DropdownBoundTemplate
            name={BOOKING_BULK_FORM_FIELDS.shiftId}
            formLabel={startCase(t(`booking.formLabel.shiftId`))}
            formAttributes={{
              dropItemsSource: "shift",
              dropItemsDisableAdd: true,
            }}
            onSelect={onShiftSelect}
          />
        )}
        <DropdownTemplate
          name={BOOKING_BULK_FORM_FIELDS.direction}
          formLabel={startCase(t(`booking.formLabel.direction`))}
          formAttributes={{
            selectItems: DIRECTIONS_INCLUDE_BOTH,
            influencedBy: {
              valueKey: BOOKING_BULK_FORM_FIELDS.direction,
              controlName: BOOKING_BULK_FORM_FIELDS.shiftId,
            },
          }}
          formValidation={requiredRuleStringField}
        />
        <BulkCreateBookingsTimeBlockStyled>
          {(direction === DirectionEnum.PickUp ||
            direction === DirectionEnum.Both) && (
            <TimeTemplate
              timeFormat={timeFormat}
              name={BOOKING_BULK_FORM_FIELDS.arrivalTime}
              formLabel={t(`booking.bulk.arrivalTime`)}
              formValidation={requiredRuleStringField}
            />
          )}
          {(direction === DirectionEnum.DropOff ||
            direction === DirectionEnum.Both) && (
            <TimeTemplate
              timeFormat={timeFormat}
              name={BOOKING_BULK_FORM_FIELDS.departureTime}
              formLabel={t(`booking.bulk.departureTime`)}
              formValidation={requiredRuleStringField}
            />
          )}
        </BulkCreateBookingsTimeBlockStyled>
        <div className="checkbox_container">
          <CheckboxTemplate
            name={BOOKING_BULK_FORM_FIELDS.shouldReplaceExisting}
            formLabel={t(`booking.formLabel.shouldReplaceExisting`)}
          />
        </div>
      </BulkCreateBookingsFormBlockStyled>
      <GroupTitleTemplate
        name="details"
        formLabel={t(`booking.bulk.details`)}
      />
      <BulkCreateBookingsFormBlockStyled>
        <SourceDestinationDropdowns
          direction={direction}
          passengersIds={passengerIds}
        />
        {useFoods && (
          <>
            <GroupTitleTemplate
              name="mealTitle"
              formLabel={t(`booking.formLabel.mealTitle`)}
            />
            <DropdownBoundTemplate
              name={BOOKING_BULK_FORM_FIELDS.mealId}
              formLabel={t(`booking.formLabel.mealId`)}
              formAttributes={{
                dropItemsSource: "meal",
                dropItemsDisableAdd: true,
                // Filter meals by the selected shift only when the shift dropdown is rendered —
                // with disableShiftsDropdown, shiftId is never populated and the ancestor filter
                // would leave the meal dropdown permanently disabled.
                ...(!disableShiftsDropdown && {
                  dropdownItemsFilter: {
                    ancestorFieldName: BOOKING_BULK_FORM_FIELDS.shiftId,
                    ancestorItemsSource: "shift",
                    matchAncestorItemArrayKey: "mealIds",
                  },
                }),
              }}
            />
          </>
        )}
        <div className="checkbox_container">
          <CheckboxTemplate
            name={BOOKING_BULK_FORM_FIELDS.isDraft}
            formLabel={t(`booking.formLabel.isDraft`)}
          />
        </div>
        {enableFlights && (
          <DropdownBoundTemplate
            name={BOOKING_BULK_FORM_FIELDS.flightId}
            formLabel={startCase(t(`booking.formLabel.flightId`))}
            formAttributes={{
              dropItemsSource: "flight",
              dropItemsDisableAdd: true,
            }}
          />
        )}
      </BulkCreateBookingsFormBlockStyled>
      <BulkConflictsSection />
    </DrawerFormWrapperStyled>
  );
};
